// ═══════════════════════════════════════════════════════════════════════════
// creditCosts.js — single source of truth for per-action credit prices.
//
// Mini-spec #5 collapsed the action map to a tighter list and re-priced
// the heaviest action (episode build) so a Free user with 3 credits/day
// can land roughly one episode every two days. The legacy keys at the
// bottom are kept as no-cost shims so any unmigrated callers don't crash —
// they're scheduled for deletion in a follow-up sweep.
// ═══════════════════════════════════════════════════════════════════════════

export const CREDIT_COSTS = {
  // Mini-spec #5 canonical actions
  // Episode builds are tiered by the generation-model selector in the
  // create chat (src/lib/modelConfig.js GENERATION_TIERS): draft → Haiku,
  // simple/standard → Sonnet, premium → Opus. Prices track the real
  // relative cost of those models (~0.5× / 1× / ~1.7×).
  episode_build_draft:         3,
  episode_build_simple:        6,
  episode_build_premium:       10,
  cover_art_flux_pro:          8,
  title_regen:                 1,
  hook_regen:                  1,
  agent_rerun:                 2,
  trend_refresh:               1,
  deep_dive:                   2,
  sponsor_match:               3,
  // Exporting an episode's narration MP3 costs a credit. Generating +
  // listening is free (audio is generated once and cached in Storage);
  // only the download/export is metered.
  audio_export:                1,

  // Legacy keys — kept as no-cost shims while we sweep callers. Will be
  // removed once every reference points at the canonical list above.
  episode_build_advanced:      6,   // collapsed into the same simple price
  episode_section_regen:       2,   // matches agent_rerun
  chat_with_producer_per_10_msgs: 0,
  cover_art_imagen_fast:       0,
  thumbnail_ideogram:          0,
  ai_show_notes:               0,
  viral_clip_prediction:       0,
  episode_remix:               0,
  trend_deep_report:           2,   // matches deep_dive
  short_form_script:           0,
  // Overlay hooks now cost 1 credit per regenerate. The first
  // generation is gated client-side (the user calls it explicitly via
  // the "Generate text overlay hook" button), but every subsequent
  // sparkle-regenerate burns 1 credit. Tiny price, real signal that
  // we're using a Claude call to write fresh psychology-grade copy.
  overlay_hooks:               1,
  // First-time captions stay free (bundled with short_form_script). Each
  // user-initiated regenerate costs 1 credit, same shape as overlay_hooks.
  captions:                    0,
  caption_regen:               1,
  // Per-section regen on the short-form result (hook / body / close).
  // Same price for all three so the UI doesn't have to special-case.
  short_form_section_regen:    1,
};

export function costOf(action) {
  const price = CREDIT_COSTS[action];
  if (price == null) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`costOf: unknown action "${action}" — charging 0 credits`);
    }
    return 0;
  }
  return price;
}

// Human-readable labels for the cost table on the pricing page.
export const ACTION_LABELS = {
  episode_build_draft:            'Build an episode (v4.5 fast)',
  episode_build_simple:           'Build an episode',
  episode_build_premium:          'Build an episode (v4.8 max)',
  cover_art_flux_pro:             'AI cover art (Flux Pro)',
  title_regen:                    'Regenerate title',
  hook_regen:                     'Regenerate hook',
  agent_rerun:                    'Re-run an agent / section',
  trend_refresh:                  'Refresh trending topics',
  deep_dive:                      'Deep-dive research report',
  sponsor_match:                  'Match with a sponsor',
  overlay_hooks:                  'Regenerate overlay hook',
  caption_regen:                  'Regenerate captions',
  short_form_section_regen:       'Regenerate hook / body / close',
};

// Time-saved estimates per action, used by the retention "time saved"
// counter on the dashboard. Each successful action increments the user's
// running tally.
export const TIME_SAVED_MINUTES = {
  episode_build_simple:        180,  // 3 hrs vs traditional pre-prod
  cover_art_flux_pro:           45,  // designer turnaround
  title_regen:                  15,
  hook_regen:                   15,
  agent_rerun:                  20,
  trend_refresh:                25,
  deep_dive:                    60,
  sponsor_match:                90,  // outreach + pitch deck time
};

export function timeSavedFor(action) {
  return TIME_SAVED_MINUTES[action] || 0;
}
